/**
 * Service Model Foundation — Phase 1 (revised)
 *
 * Single source of truth for the Telecom Service Model taxonomy and for
 * deciding which questionnaires use it.
 *
 * Mirrors the server side:
 *   • public.telecom_service_model_allowlist()
 *   • public.questionnaire_capabilities  (is_telecom_questionnaire /
 *     questionnaire_requires_service_models)
 *
 * NOTE: this module does NOT drive question applicability. It governs where the
 * Service Model selector appears, whether it is mandatory, and how values are
 * normalised before they reach the database.
 */

/** Sector-specific operating-profile dimension. Telecom is the only one in Phase 1. */
export type ProfileDimension = "telecom_service_model";

/** The 12 canonical values actually rendered by the UI and stored in the DB. */
export const SERVICE_MODEL_OPTIONS: string[] = [
  "MNO",
  "MVNO",
  "ISP",
  "FiberCo",
  "TowerCo",
  "Wholesale Carrier",
  "Neutral Host",
  "Private Network Operator",
  "OTT Provider",
  "Hyperscaler / TechCo",
  "IoT / M2M Operator",
  "Other",
];

/**
 * Canonical value → applicability/weighting blueprint key.
 * The blueprint historically used the short key `Hyperscaler`; stored values are
 * never rewritten, they are mapped.
 */
const BLUEPRINT_KEY_BY_CANONICAL: Record<string, string> = {
  "Hyperscaler / TechCo": "Hyperscaler",
};

export function toBlueprintKey(model: string): string {
  return BLUEPRINT_KEY_BY_CANONICAL[model] ?? model;
}

/**
 * Explicit questionnaire capability configuration. Mirrors the seed of
 * public.questionnaire_capabilities. No prefix matching: a questionnaire must be
 * listed here (and seeded server-side) to use Service Models.
 */
export const QUESTIONNAIRE_CAPABILITIES: Record<
  string,
  { profileDimension: ProfileDimension; uses: boolean; requires: boolean }
> = {
  telecom_v2: { profileDimension: "telecom_service_model", uses: true, requires: true },
  telecom_self_v1: { profileDimension: "telecom_service_model", uses: true, requires: false },
};

/** True when the questionnaire uses Telecom Service Models at all. */
export function questionnaireUsesServiceModels(questionnaireId?: string | null): boolean {
  if (!questionnaireId) return false;
  return QUESTIONNAIRE_CAPABILITIES[questionnaireId]?.uses === true;
}

/** True when at least one Service Model must be selected before creation. */
export function serviceModelsRequired(questionnaireId?: string | null): boolean {
  if (!questionnaireId) return false;
  return QUESTIONNAIRE_CAPABILITIES[questionnaireId]?.requires === true;
}

/** The sector operating-profile dimension a questionnaire uses, if any. */
export function getProfileDimension(questionnaireId?: string | null): ProfileDimension | null {
  if (!questionnaireId) return null;
  return QUESTIONNAIRE_CAPABILITIES[questionnaireId]?.profileDimension ?? null;
}

/**
 * @deprecated Prefer questionnaireUsesServiceModels. Kept as a thin alias so the
 * existing call sites keep compiling; it no longer does prefix matching.
 */
export function isTelecomQuestionnaire(
  a?: string | null,
  b?: string | null,
): boolean {
  // Legacy call sites pass (category, questionnaireId); newer ones pass just the id.
  const questionnaireId = b ?? a;
  return questionnaireUsesServiceModels(questionnaireId);
}

/** Normalises a selection exactly the way public.normalize_service_models does. */
export function normalizeServiceModels(models: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of models ?? []) {
    const m = (raw ?? "").trim();
    if (!m) continue;
    const key = m.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(m);
  }
  return out.sort((a, b) => a.localeCompare(b));
}

/**
 * Client-side mirror of the server validation, for pre-submit feedback.
 *
 * @param models      selected canonical values
 * @param options.required  whether an empty selection is an error
 * @param options.otherDescription  companion text required when "Other" is picked
 */
export function validateServiceModels(
  models: string[],
  options: { required?: boolean; otherDescription?: string | null } = {},
): { ok: boolean; error?: string } {
  const { required = true, otherDescription } = options;
  const normalized = normalizeServiceModels(models);

  if (normalized.length === 0) {
    return required
      ? { ok: false, error: "At least one Telecom Service Model is required." }
      : { ok: true };
  }

  const invalid = normalized.filter((m) => !SERVICE_MODEL_OPTIONS.includes(m));
  if (invalid.length > 0) {
    return { ok: false, error: `Unknown Service Model: ${invalid.join(", ")}` };
  }

  if (normalized.includes("Other") && !(otherDescription ?? "").trim()) {
    return {
      ok: false,
      error: 'Describe the operating model when selecting "Other".',
    };
  }

  return { ok: true };
}
